// Utility functions for managing user favorites
import api from '../services/api';
import { MenuItem } from '../components/Layout/Sidebar';

export interface FavoriteItem {
  title: string;
  to: string;
  fullPath?: string | null;
  icon?: React.ReactNode;
}

// localStorage key for caching favorites
const FAVORITES_CACHE_KEY = 'ums_favorites_cache';

// In-flight request deduplication: concurrent getFavorites() calls share one request
let favoritesFetchPromise: Promise<FavoriteItem[]> | null = null;
// In-memory cache to avoid refetching when effects re-run (e.g. dependency changes)
const FAVORITES_MEMORY_TTL_MS = 30_000; // 30 seconds
let favoritesMemoryCache: { data: FavoriteItem[]; at: number } | null = null;

/**
 * Get cached favorites from localStorage (synchronous, instant)
 */
export const getCachedFavorites = (): FavoriteItem[] => {
  try {
    const cached = localStorage.getItem(FAVORITES_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (error) {
    console.warn('Error reading favorites cache:', error);
  }
  return [];
};

/**
 * Save favorites to localStorage cache
 */
const setCachedFavorites = (favorites: FavoriteItem[]): void => {
  try {
    // Only cache serializable data (no icons)
    const serializableFavorites = favorites.map(fav => ({
      title: fav.title,
      to: fav.to,
      fullPath: fav.fullPath || null,
    }));
    localStorage.setItem(FAVORITES_CACHE_KEY, JSON.stringify(serializableFavorites));
  } catch (error) {
    console.warn('Error saving favorites cache:', error);
  }
};

/**
 * Clear favorites cache (call on logout)
 */
export const clearFavoritesCache = (): void => {
  favoritesFetchPromise = null;
  favoritesMemoryCache = null;
  try {
    localStorage.removeItem(FAVORITES_CACHE_KEY);
  } catch (error) {
    console.warn('Error clearing favorites cache:', error);
  }
};

/**
 * Get all favorites for the current user from API
 * Also updates the localStorage cache for future instant loading.
 * Concurrent calls are deduplicated; a short in-memory TTL avoids refetch on every effect re-run.
 */
export const getFavorites = async (): Promise<FavoriteItem[]> => {
  const token = localStorage.getItem('access_token');
  if (!token) {
    clearFavoritesCache();
    return [];
  }

  const now = Date.now();
  if (favoritesMemoryCache && now - favoritesMemoryCache.at < FAVORITES_MEMORY_TTL_MS) {
    return favoritesMemoryCache.data;
  }

  if (favoritesFetchPromise) {
    return favoritesFetchPromise;
  }

  const doFetch = async (): Promise<FavoriteItem[]> => {
    try {
      const response = await api.get('/profile/favorites');
      let favorites: FavoriteItem[] = [];
      if (response.data && Array.isArray(response.data.data)) {
        favorites = response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        favorites = response.data;
      } else if (Array.isArray(response)) {
        favorites = response;
      }
      setCachedFavorites(favorites);
      favoritesMemoryCache = { data: favorites, at: Date.now() };
      return favorites;
    } catch (error: any) {
      console.error('Error fetching favorites:', error);
      if (error.response?.status === 401 || error.message?.includes('401') || error.message?.includes('Unauthorized') || error.message?.includes('expired')) {
        clearFavoritesCache();
        return [];
      }
      return getCachedFavorites();
    } finally {
      favoritesFetchPromise = null;
    }
  };

  favoritesFetchPromise = doFetch();
  return favoritesFetchPromise;
};

/**
 * Save favorites to API and update cache
 */
const saveFavorites = async (favorites: FavoriteItem[]): Promise<boolean> => {
  try {
    // Check if user is authenticated before making the call
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error("User not authenticated. Please log in.");
    }

    // Ensure only serializable data (no React elements)
    const serializableFavorites = favorites.map(fav => ({
      title: fav.title,
      to: fav.to,
      fullPath: fav.fullPath || null,
    }));

    // Update caches immediately (optimistic)
    setCachedFavorites(serializableFavorites);
    favoritesMemoryCache = { data: serializableFavorites, at: Date.now() };

    await api.put('/profile/favorites', { favorites: serializableFavorites });
    // Trigger custom event for same-tab updates
    window.dispatchEvent(new Event('favoritesUpdated'));
    return true;
  } catch (error: any) {
    // Handle 401 (Unauthorized) errors gracefully
    if (error.response?.status === 401 || error.message?.includes('401') || error.message?.includes('Unauthorized') || error.message?.includes('expired')) {
      throw new Error("Your session has expired. Please log in again to save favorites.");
    }
    throw error;
  }
};

/**
 * Add an item to favorites
 * Only stores serializable data (title, to) - not React elements
 */
export const addToFavorites = async (item: { title: string; to: string; fullPath?: string }): Promise<boolean> => {
  try {
    const favorites = await getFavorites();
    // Check if item already exists (by route)
    if (!favorites.find(fav => fav.to === item.to)) {
      // Store only serializable data - no React elements
      const favoriteItem: FavoriteItem = {
        title: item.title,
        to: item.to,
        fullPath: item.fullPath || null,
      };
      favorites.push(favoriteItem);
      const result = await saveFavorites(favorites);
      return result;
    }
    return false;
  } catch (error) {
    throw error;
  }
};

/**
 * Add multiple items to favorites in a single operation
 * Prevents race conditions when adding multiple items at once
 */
export const addMultipleToFavorites = async (items: Array<{ title: string; to: string; fullPath?: string }>): Promise<{ success: boolean; added: number; message: string }> => {
  try {
    const favorites = await getFavorites();
    
    const favoriteRoutes = favorites.map(fav => fav.to);
    const newItems: FavoriteItem[] = items
      .filter(item => !favoriteRoutes.includes(item.to)) // Only add items not already in favorites
      .map(item => ({
        title: item.title,
        to: item.to,
        fullPath: item.fullPath || null,
      }));
    
    if (newItems.length === 0) {
      return { success: false, added: 0, message: 'All items are already in favorites' };
    }
    
    const updatedFavorites = [...favorites, ...newItems];
    const result = await saveFavorites(updatedFavorites);
    
    return { success: result, added: newItems.length, message: `Added ${newItems.length} item(s) to favorites` };
  } catch (error) {
    throw error;
  }
};

/**
 * Remove an item from favorites
 */
export const removeFromFavorites = async (route: string): Promise<boolean> => {
  try {
    const favorites = await getFavorites();
    const filtered = favorites.filter(fav => fav.to !== route);
    return await saveFavorites(filtered);
  } catch (error) {
    return false;
  }
};

/**
 * Reorder favorites - move item from one position to another
 */
export const reorderFavorites = async (favorites: FavoriteItem[]): Promise<boolean> => {
  try {
    return await saveFavorites(favorites);
  } catch (error) {
    console.error('Error reordering favorites:', error);
    return false;
  }
};

/**
 * Check if an item is in favorites
 */
export const isFavorite = async (route: string): Promise<boolean> => {
  const favorites = await getFavorites();
  return favorites.some(fav => fav.to === route);
};

/**
 * Get all available menu items (flattened) for adding to favorites.
 * Includes every page: top-level path, each view (List View, Monthly View, etc.), and each child's views.
 */
export const getAllMenuItems = (menuItems: MenuItem[]): Array<{ title: string; to: string; icon?: React.ReactNode; fullPath: string }> => {
  const items: Array<{ title: string; to: string; icon?: React.ReactNode; fullPath: string }> = [];

  const flattenItems = (itemsArray: MenuItem[], parentTitle = '') => {
    itemsArray.forEach(item => {
      // Skip Favorites (dynamic, not a real page to pin)
      if (item.name === 'Favorites') return;

      const fullPathPrefix = parentTitle ? `${parentTitle} > ${item.name}` : item.name;

      if (item.views && item.views.length > 0) {
        // Add each view as a separate page (e.g. "Todos - List View", "Todos - Monthly View")
        item.views.forEach(view => {
          items.push({
            title: item.views!.length > 1 ? `${item.name} – ${view.label}` : item.name,
            to: view.path,
            icon: view.icon ?? item.icon,
            fullPath: item.views!.length > 1 ? `${fullPathPrefix} > ${view.label}` : fullPathPrefix,
          });
        });
      } else if (item.path) {
        // Item with direct path (no views)
        items.push({
          title: item.name,
          to: item.path,
          icon: item.icon,
          fullPath: fullPathPrefix,
        });
      }

      if (item.children && item.children.length > 0) {
        flattenItems(item.children, fullPathPrefix);
      }
    });
  };

  flattenItems(menuItems);
  return items;
};

/**
 * Reconstruct icon from stored favorites by finding it in original menuItems (including views)
 */
export const reconstructFavoriteIcon = (favorite: FavoriteItem, allMenuItems: MenuItem[]): React.ReactNode | undefined => {
  const findIconByRoute = (items: MenuItem[], route: string): React.ReactNode | undefined => {
    for (const item of items) {
      if (item.path === route) return item.icon;
      if (item.views) {
        const view = item.views.find(v => v.path === route);
        // For favorites, always prefer the parent module icon so that
        // items like "Services" show the Services module icon instead
        // of a generic view icon (e.g. list/card).
        if (view) return item.icon ?? view.icon;
      }
      if (item.children) {
        const icon = findIconByRoute(item.children, route);
        if (icon) return icon;
      }
    }
    return undefined;
  };
  return findIconByRoute(allMenuItems, favorite.to);
};

